import React, { useEffect, useState } from "react";
import { Grid, Switch, Typography, Slider, Divider, IconButton } from "@mui/material";
import { useAgencyInfo } from "context/agency";
import CloseIcon from "@mui/icons-material/Close";
import FormField from "layouts/pages/account/components/FormField";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SettingsCard from "./SettingsCard";
import MapPinLimitSection from "./MapPinLimitSection";
import banner from "assets/images/banner.png";

const MAP_ZOOM_BIAS_MARKS = [
  { value: -2, label: "Furthest" },
  { value: -1, label: "Further" },
  { value: 0, label: "Default" },
  { value: 1, label: "Closer" },
  { value: 2, label: "Closest" },
];

const clampMapZoomBias = (value) => {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return 0;
  return Math.min(2, Math.max(-2, n));
};

const HeroContentSection = ({
  inputData,
  showMap,
  showMapPins,
  agencyId,
  onMapPinLimitSaved,
  handleToggleChange,
  handleShowMapPinsChange,
  main_image,
  previewMainImage,
  handleImageUpload,
  onRemoveMainImage,
  onGhlPick,
  autoSaveField,
}) => {
  const [agency] = useAgencyInfo();
  const hasGoogleMapsApi = Boolean(agency?.google_api_key?.trim());
  const [height, setHeight] = useState(inputData?.hero_section_image_height || 500);
  const [heroTextheadingSize, setHeroTextHeadingSize] = useState(
    inputData?.hero_text_heading_size || 36
  );
  const [heroTextSubheadingSize, setHeroTextSubHeadingSize] = useState(
    inputData?.hero_text_sub_heading_size || 18
  );
  const [mapZoomBias, setMapZoomBias] = useState(
    clampMapZoomBias(inputData?.homepageMapZoomBias ?? 0)
  );

  const save = (name, value) => {
    if (autoSaveField) autoSaveField(name, value);
  };
  const handleBlurSave = (e) => save(e.target.name, e.target.value);

  const SEARCH_SIZES = {
    small: {
      search_bar_width: 360,
      search_bar_height: 45,
      search_bar_text_size: 14,
      search_button_width: 70,
      search_button_height: 40,
      search_button_text_size: 14,
    },
    medium: {
      search_bar_width: 420,
      search_bar_height: 50,
      search_bar_text_size: 16,
      search_button_width: 80,
      search_button_height: 50,
      search_button_text_size: 16,
    },
    large: {
      search_bar_width: 520,
      search_bar_height: 60,
      search_bar_text_size: 18,
      search_button_width: 100,
      search_button_height: 55,
      search_button_text_size: 18,
    },
    xlarge: {
      search_bar_width: 620,
      search_bar_height: 70,
      search_bar_text_size: 20,
      search_button_width: 120,
      search_button_height: 65,
      search_button_text_size: 20,
    },
  };

  const SIZE_KEYS = ["small", "medium", "large", "xlarge"];

  const [searchSize, setSearchSize] = useState(inputData?.search_size || "medium");

  const handleSearchSizeChange = (e, value) => {
    const sizeKey = SIZE_KEYS[value];
    setSearchSize(sizeKey);
  };
  const handleSearchSizeCommit = (e, value) => {
    const sizeKey = SIZE_KEYS[value];
    save("search_size", sizeKey);
  };

  useEffect(() => {
    if (inputData?.homepageMapZoomBias !== undefined) {
      setMapZoomBias(clampMapZoomBias(inputData.homepageMapZoomBias));
    }
  }, [inputData?.homepageMapZoomBias]);

  return (
    <>
      <SettingsCard
        title="Hero Section"
        subtitle="Configure hero section content and styling"
        gridProps={{
          xs: 12,
          sx: {
            "@media (min-width: 1100px)": {
              flexBasis: "calc(50% - 12px)",
              maxWidth: "calc(50% - 12px)",
              width: "calc(50% - 12px)",
              marginRight: "24px",
            },
          },
        }}
      >
        <SoftTypography fontWeight="bold" mb={1}>
          Hero Image
        </SoftTypography>

        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <SoftBox
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              mb={0}
              sx={{
                width: "100%",
                maxWidth: "600px",
              }}
            >
              <SoftBox
                sx={{
                  width: "100%",
                  maxWidth: "600px",
                  height: "120px",
                  border: "1.5px solid #e0e0e0",
                  borderRadius: "12px",
                  padding: "12px",
                  backgroundColor: "#f9f9f9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  cursor: "pointer",
                  position: "relative",
                  mb: 0.25,
                }}
                onClick={() => {
                  if (onGhlPick) {
                    onGhlPick();
                    return;
                  }
                  document.getElementById("main_image_input")?.click();
                }}
              >
                <input type="hidden" name="main_image" value={main_image} />
                {main_image && onRemoveMainImage && (
                  <IconButton
                    size="small"
                    aria-label="Remove hero image"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveMainImage();
                    }}
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      zIndex: 1,
                      backgroundColor: "rgba(0, 0, 0, 0.55)",
                      color: "white",
                      "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.75)" },
                      width: 24,
                      height: 24,
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
                {previewMainImage || banner ? (
                  <>
                    <img
                      src={previewMainImage || banner}
                      alt="main_image"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        width: "auto",
                        height: "auto",
                        objectFit: "contain",
                        borderRadius: "8px",
                      }}
                    />
                    {!onGhlPick && (
                      <input
                        id="main_image_input"
                        hidden
                        accept="image/*"
                        type="file"
                        onChange={(e) => {
                          handleImageUpload(e, "main_image");
                          e.target.value = "";
                        }}
                      />
                    )}
                  </>
                ) : onGhlPick ? (
                  <SoftButton
                    variant="outlined"
                    color="dark"
                    onClick={(e) => {
                      e.stopPropagation();
                      onGhlPick();
                    }}
                  >
                    Choose from Media
                  </SoftButton>
                ) : (
                  <SoftButton variant="outlined" component="label" color="dark">
                    Upload Main Image
                    <input
                      id="main_image_input"
                      hidden
                      accept="image/*"
                      type="file"
                      onChange={(e) => {
                        handleImageUpload(e, "main_image");
                        e.target.value = "";
                      }}
                    />
                  </SoftButton>
                )}
              </SoftBox>
              <SoftTypography
                variant="caption"
                sx={{
                  fontSize: "11px",
                  color: "#666",
                  marginTop: "6px",
                }}
              >
                Recommended: (1920px x 700px) 5:1 ratio. 1MB size.
              </SoftTypography>
            </SoftBox>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography sx={{ mb: 1 }}>Hero Image Height: {height}px</Typography>

            <Slider
              value={height}
              min={500}
              max={700}
              step={10}
              onChange={(e, val) => setHeight(val)}
              onChangeCommitted={(e, val) => save("hero_section_image_height", val)}
            />

            <input type="hidden" name="hero_section_image_height" value={height} />
          </Grid>

          <Grid item xs={12}>
            <SoftBox display="flex" alignItems="center" mb={0.5}>
              <Switch checked={showMap} onChange={handleToggleChange} />
              <SoftTypography variant="body2" sx={{ marginLeft: "10px", fontSize: "14px" }}>
                Display Map
              </SoftTypography>
            </SoftBox>
            {!hasGoogleMapsApi && (
              <SoftTypography
                variant="caption"
                sx={{ color: "#d32f2f", display: "block", mb: 1, fontSize: "13px", lineHeight: 1.4 }}
              >
                Google Maps API not connected. Please go to Settings &gt; Integrations to connect
                now.
              </SoftTypography>
            )}
          </Grid>

          {showMap && (
            <Grid item xs={12}>
              <SoftBox display="flex" alignItems="center" mb={1}>
                <Switch checked={showMapPins} onChange={handleShowMapPinsChange} />
                <SoftTypography variant="body2" sx={{ marginLeft: "10px", fontSize: "14px" }}>
                  Show business pins on map
                </SoftTypography>
              </SoftBox>
              <MapPinLimitSection
                agencyId={agencyId}
                showMap={showMap}
                showMapPins={showMapPins}
                onLimitSaved={onMapPinLimitSaved}
              />
              {showMapPins && (
                <SoftBox mt={2} sx={{ maxWidth: 520, px: 0.5 }}>
                  <Typography sx={{ mb: 0.5, fontSize: "14px", fontWeight: 600 }}>
                    Homepage map zoom
                  </Typography>
                  <SoftTypography
                    variant="caption"
                    sx={{ color: "#666", display: "block", mb: 1.5, fontSize: "12px" }}
                  >
                    Default keeps the auto zoom that fits all pins. Move one or two steps closer or
                    further from that view.
                  </SoftTypography>
                  <Slider
                    value={mapZoomBias}
                    min={-2}
                    max={2}
                    step={1}
                    marks={MAP_ZOOM_BIAS_MARKS}
                    onChange={(e, val) => setMapZoomBias(val)}
                    onChangeCommitted={(e, val) => save("homepageMapZoomBias", val)}
                    valueLabelDisplay="off"
                    sx={{
                      mt: 1,
                      mb: 1,
                      "& .MuiSlider-markLabel": {
                        fontSize: "11px",
                        color: "#666",
                      },
                    }}
                  />
                </SoftBox>
              )}
            </Grid>
          )}
        </Grid>
        <Divider sx={{ my: 3 }} />
        <SoftTypography fontWeight="bold" mb={1}>
          Hero Text
        </SoftTypography>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormField
              label="Hero Title"
              name="title"
              defaultValue={inputData?.title || "Find Local Businesses You Can Trust"}
              onBlur={handleBlurSave}
            />
          </Grid>

          <Grid item xs={6}>
            <FormField
              label="Hero Subtitle"
              name="sub_title"
              defaultValue={inputData?.sub_title || "Search by name, category, or keyword — and support the businesses that make your community thrive."}
              onBlur={handleBlurSave}
            />
          </Grid>

          <Grid item xs={6}>
            <FormField
              label="Heading Color"
              name="hero_text_heading"
              inputProps={{ type: "color" }}
              defaultValue={inputData?.hero_text_heading}
              onBlur={handleBlurSave}
            />
          </Grid>

          <Grid item xs={6}>
            <FormField
              label="Subheading Color"
              name="hero_text_sub_heading"
              inputProps={{ type: "color" }}
              defaultValue={inputData?.hero_text_sub_heading}
              onBlur={handleBlurSave}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography sx={{ mb: 1 }}>Heading Size: {heroTextheadingSize}px</Typography>

            <Slider
              value={heroTextheadingSize}
              min={24}
              max={90}
              step={1}
              onChange={(e, val) => setHeroTextHeadingSize(val)}
              onChangeCommitted={(e, val) => save("hero_text_heading_size", val)}
            />

            <input type="hidden" name="hero_text_heading_size" value={heroTextheadingSize} />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography sx={{ mb: 1 }}>Subheading Size: {heroTextSubheadingSize}px</Typography>

            <Slider
              value={heroTextSubheadingSize}
              min={12}
              max={50}
              step={1}
              onChange={(e, val) => setHeroTextSubHeadingSize(val)}
              onChangeCommitted={(e, val) => save("hero_text_sub_heading_size", val)}
            />

            <input type="hidden" name="hero_text_sub_heading_size" value={heroTextSubheadingSize} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* SEARCH BAR */}

        <SoftTypography fontWeight="bold" mb={1}>
          Search Bar Size
        </SoftTypography>
        <Grid item xs={12} md={6} paddingLeft={2}>
          <Slider
            value={SIZE_KEYS.indexOf(searchSize)}
            onChange={handleSearchSizeChange}
            onChangeCommitted={handleSearchSizeCommit}
            step={1}
            min={0}
            max={3}
            marks={[
              { value: 0, label: "Small" },
              { value: 1, label: "Medium" },
              { value: 2, label: "Large" },
              { value: 3, label: "XL" },
            ]}
          />
          <input type="hidden" name="search_size" value={searchSize} />
        </Grid>

        <Divider sx={{ my: 3 }} />

        <SoftTypography fontWeight="bold" mb={1}>
          Hero search placeholder
        </SoftTypography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <FormField
              label="Placeholder text"
              name="home_hero_search_placeholder"
              placeholder="Leave empty for default"
              defaultValue={inputData?.home_hero_search_placeholder ?? ""}
              inputProps={{ maxLength: 160 }}
              onBlur={handleBlurSave}
            />
            <SoftTypography
              variant="caption"
              sx={{ fontSize: "11px", color: "#666", mt: 0.5, display: "block" }}
            >
              Default: Search by name, tag, city, state, country or phone
            </SoftTypography>
          </Grid>
        </Grid>
      </SettingsCard>
    </>
  );
};

export default HeroContentSection;
